import { useEffect, useState } from "react";
import type { ImageDTO } from "@shared/types";
import { api, ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import { Modal } from "./Modal";
import { Button } from "./Button";

interface Props {
  image: ImageDTO | null;
  onClose: () => void;
  onDone: (updated: ImageDTO) => void;
}

export function RenameDialog({ image, onClose, onDone }: Props) {
  const toast = useToast();
  const [base, setBase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (image) {
      setBase(image.filename.replace(/\.[^.]+$/, ""));
      setError(null);
    }
  }, [image]);

  const submit = async () => {
    if (!image) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.renameImage(image.id, base.trim());
      toast.success("Renamed. The direct URL has changed.");
      onDone(res.image);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not rename.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!image}
      onClose={busy ? () => {} : onClose}
      title="Rename image"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            Rename
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-stretch">
          <input
            autoFocus
            value={base}
            onChange={(e) => setBase(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="field w-full rounded-r-none"
          />
          <span className="grid flex-shrink-0 place-items-center rounded-r-lg border border-l-0 border-zinc-900/10 bg-white/50 px-3 text-sm text-zinc-500">
            .{image?.extension}
          </span>
        </div>
        <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-800">
          The direct URL changes when you rename. Existing links to the old URL stop working.
        </p>
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-[13px] text-red-700">{error}</p>
        )}
      </div>
    </Modal>
  );
}
